import { ClaimVerification } from "@/types/summary";
import { AnnotationContent } from "./AnnotationContent";
import { AnnotationCard } from "./AnnotationCard";
import { NavigationControls } from "./NavigationControls";

type AnnotationType =
  "quote" | "content_flag" | "motif" | "theme" | "trope" | "claim";

interface BaseAnnotationProps {
  type: Exclude<AnnotationType, "claim">;
  content?: string;
  quotedContent?: string;
  categories?: string[];
  isSelected: boolean;
  onSelect: () => void;
  isMobile?: boolean;
  // Navigation props (optional for regular annotations)
  currentIndex?: number;
  totalCount?: number;
  onNavigateNext?: () => void;
  onNavigatePrevious?: () => void;
}

interface ClaimAnnotationProps {
  type: "claim";
  claim: ClaimVerification;
  currentIndex: number;
  totalCount: number;
  isSelected: boolean;
  onSelect: () => void;
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  onExpandToggle?: () => void;
  isMobile?: boolean;
}

type UnifiedAnnotationProps = BaseAnnotationProps | ClaimAnnotationProps;

function isClaimProps(
  props: UnifiedAnnotationProps,
): props is ClaimAnnotationProps {
  return props.type === "claim";
}

export function UnifiedAnnotation(props: UnifiedAnnotationProps) {
  const { isSelected, onSelect, isMobile = false } = props;

  // Render content
  const content = (() => {
    if (isClaimProps(props)) {
      const { claim, onExpandToggle } = props;
      return (
        <AnnotationContent
          type="claim"
          claim={claim}
          onExpandToggle={onExpandToggle}
        />
      );
    }
    const { type, content: contentText, quotedContent, categories } = props;
    return (
      <AnnotationContent
        type={type}
        content={contentText}
        quotedContent={quotedContent}
        categories={categories}
      />
    );
  })();

  // Render navigation if provided
  const navigation = (() => {
    if (isClaimProps(props)) {
      const { currentIndex, totalCount, onNavigatePrevious, onNavigateNext } =
        props;
      return (
        <NavigationControls
          currentIndex={currentIndex}
          totalCount={totalCount}
          isActive={isSelected}
          onPrevious={onNavigatePrevious}
          onNext={onNavigateNext}
          compact={!isMobile} // Compact for desktop
        />
      );
    }
    const { currentIndex, totalCount, onNavigateNext, onNavigatePrevious } =
      props;
    if (
      currentIndex !== undefined &&
      totalCount !== undefined &&
      onNavigateNext &&
      onNavigatePrevious
    ) {
      return (
        <NavigationControls
          currentIndex={currentIndex}
          totalCount={totalCount}
          isActive={isSelected}
          onPrevious={onNavigatePrevious}
          onNext={onNavigateNext}
          compact={!isMobile} // Compact for desktop
        />
      );
    }
    return null;
  })();

  return (
    <AnnotationCard
      isSelected={isSelected}
      isMobile={isMobile}
      onSelect={onSelect}
      navigation={navigation}
    >
      {content}
    </AnnotationCard>
  );
}
